import { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import axios from "axios";
import Header from "../component/header";
import Footer from "../component/Footer";
import { UserDataContext } from "../context/User";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser } = useContext(UserDataContext);

  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const verify = async () => {
      const sessionId = searchParams.get("session_id");
      const isUpgrade = searchParams.get("upgrade") === "true";
      const paymentType = searchParams.get("type");

      if (!sessionId) {
        setStatus("error");
        setMessage("No payment session found.");
        return;
      }

      try {
        const verifyUrl = paymentType === "quest_entry"
          ? `/api/quest/verify-payment?session_id=${sessionId}`
          : `/api/subscriptions/verify-payment?session_id=${sessionId}`;
        const res = await axios.get(
          verifyUrl,
          { withCredentials: true }
        );

        if (res.data.success) {
          // Refresh user data so tier updates everywhere
          await fetchUser();
          setStatus("success");
          setMessage(paymentType === "quest_entry"
            ? "Your quest event entry payment has been confirmed."
            : isUpgrade
            ? "Your membership has been upgraded successfully!"
            : "Payment confirmed. Welcome to LWBL!"
          );
          // Auto-redirect after 3s
          setTimeout(() => navigate(paymentType === "quest_entry" ? "/submissions" : "/membership"), 3000);
        } else {
          setStatus("error");
          setMessage(res.data.message || "Payment verification failed.");
        }
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.message || "Could not verify payment. Please contact support.");
      }
    };

    verify();
  }, []);

  return (
    <div>
      <Header />
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-20">
        <div className="max-w-md w-full text-center bg-white rounded-3xl shadow-lg p-10">

          {status === "verifying" && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-cyan-50 flex items-center justify-center mx-auto mb-6">
                <Loader size={32} className="text-cyan-500 animate-spin" strokeWidth={1.5} />
              </div>
              <h2 className="font-cinzel text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
              <p className="font-outfit text-gray-500">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-green-500" strokeWidth={1.5} />
              </div>
              <h2 className="font-cinzel text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
              <p className="font-outfit text-gray-500 mb-6">{message}</p>
              <p className="font-outfit text-sm text-gray-400">
                Redirecting to your {searchParams.get("type") === "quest_entry" ? "submissions" : "membership"} page...
              </p>
              <button
                onClick={() => navigate(searchParams.get("type") === "quest_entry" ? "/submissions" : "/membership")}
                className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit text-sm hover:scale-105 transition-transform"
              >
                {searchParams.get("type") === "quest_entry" ? "View Submissions" : "View Membership"}
              </button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6">
                <XCircle size={32} className="text-red-400" strokeWidth={1.5} />
              </div>
              <h2 className="font-cinzel text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
              <p className="font-outfit text-gray-500 mb-6">{message}</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => navigate("/membership")}
                  className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit text-sm hover:scale-105 transition-transform"
                >
                  Back to Plans
                </button>
                <button
                  onClick={() => navigate("/contact")}
                  className="px-6 py-2.5 rounded-full border border-gray-300 text-gray-600 font-outfit text-sm hover:bg-gray-50 transition-colors"
                >
                  Contact Support
                </button>
              </div>
            </>
          )}

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
